// Middleware de Segurança - Sistema de Agendamento UP V.4.0
// Camada adicional de validações e proteções

import React, { useEffect, useState } from 'react';
import { securityService } from '../../services/securityService';
import AlertModal from '../common/AlertModal';

interface SecurityMiddlewareProps {
  children: React.ReactNode;
}

interface SecurityAlert {
  type: 'warning' | 'error' | 'info';
  message: string;
  timestamp: number;
}

export const SecurityMiddleware: React.FC<SecurityMiddlewareProps> = ({ children }) => {
  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([]);
  const [isSecure, setIsSecure] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);

  useEffect(() => {
    // Verificar ambiente seguro
    checkSecurityEnvironment();
    
    // Configurar listeners de segurança
    setupSecurityListeners();
    
    // Verificar sessão periodicamente
    const sessionCheck = setInterval(checkSessionValidity, 30000); // 30 segundos

    // Escutar evento de sessão expirada disparado pelo api.ts
    const handleSessionExpiredEvent = () => {
      handleSessionExpired();
    };
    window.addEventListener('session-expired', handleSessionExpiredEvent);
    
    return () => {
      clearInterval(sessionCheck);
      window.removeEventListener('session-expired', handleSessionExpiredEvent);
    };
  }, []);

  const checkSecurityEnvironment = () => {
    const alerts: SecurityAlert[] = [];
    
    // Em desenvolvimento, sempre considerar seguro
    if (process.env.NODE_ENV !== 'production') {
      setIsSecure(true);
      setSecurityAlerts(alerts);
      return;
    }
    
    // Verificar HTTPS em produção
    if (window.location.protocol !== 'https:') {
      alerts.push({
        type: 'error',
        message: 'Conexão não segura. Use HTTPS em produção.',
        timestamp: Date.now()
      });
      setIsSecure(false);
    }
    
    // Verificar console aberto (técnica básica)
    const devtools = checkDevTools();
    if (devtools.open) {
      alerts.push({
        type: 'warning',
        message: 'Ferramentas de desenvolvedor detectadas.',
        timestamp: Date.now()
      });
    }
    
    // Verificar tamanho da tela (possível emulação de mobile)
    if (window.innerWidth < 320 || window.innerHeight < 480) {
      alerts.push({
        type: 'warning',
        message: 'Tamanho de tela incomum detectado.',
        timestamp: Date.now()
      });
    }
    
    setSecurityAlerts(alerts);
  };

  const checkDevTools = () => {
    const threshold = 160;
    return {
      open: window.outerHeight - window.innerHeight > threshold ||
             window.outerWidth - window.innerWidth > threshold
    };
  };

  const setupSecurityListeners = () => {
    // Em desenvolvimento, não configurar listeners restritivos
    if (process.env.NODE_ENV !== 'production') {
      return () => {}; // Return empty cleanup function
    }

    // Prevenir cópia de conteúdo sensível
    const preventCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      const selectedText = selection?.toString() || '';
      
      // Verificar se contém informações sensíveis
      if (containsSensitiveData(selectedText)) {
        e.preventDefault();
        addSecurityAlert('warning', 'Cópia de conteúdo sensível bloqueada.');
      }
    };

    // Prevenir print screen
    const preventPrint = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        addSecurityAlert('warning', 'Captura de tela detectada.');
      }
    };

    document.addEventListener('copy', preventCopy);
    document.addEventListener('keydown', preventPrint);

    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('keydown', preventPrint);
    };
  };

  const containsSensitiveData = (text: string): boolean => {
    const sensitivePatterns = [
      /\d{3}\.\d{3}\.\d{3}-\d{2}/, // CPF
      /\d{2}\.\d{3}\.\d{4}\/\d{4}-\d{2}/, // CNPJ
      /\b\d{4}\s?\d{4}\s?\d{4}\s?\d{4}\b/, // Cartão de crédito
      /senha/i,
      /password/i,
      /token/i
    ];
    
    return sensitivePatterns.some(pattern => pattern.test(text));
  };

  const checkSessionValidity = () => {
    // Verificar timeout de sessão
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity);
      const sessionTimeout = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '3600000');
      
      if (timeSinceActivity > sessionTimeout) {
        // Sessão expirada
        handleSessionExpired();
      }
    }
  };

  const handleSessionExpired = () => {
    setShowSessionModal(true);
    
    // Limpar dados de sessão
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
    sessionStorage.clear();
  };

  const handleSessionRedirect = () => {
    localStorage.removeItem('@sistema-up:token');
    localStorage.removeItem('@sistema-up:refreshToken');
    window.location.href = '/login';
  };

  const addSecurityAlert = (type: 'warning' | 'error' | 'info', message: string) => {
    const alert: SecurityAlert = {
      type,
      message,
      timestamp: Date.now()
    };
    
    setSecurityAlerts(prev => [...prev.slice(-4), alert]); // Manter apenas 5 alertas recentes
  };

  const dismissAlert = (timestamp: number) => {
    setSecurityAlerts(prev => prev.filter(alert => alert.timestamp !== timestamp));
  };

  const getAlertColor = (type: 'warning' | 'error' | 'info') => {
    switch (type) {
      case 'error':
        return 'bg-red-500 border-red-600';
      case 'warning':
        return 'bg-yellow-500 border-yellow-600';
      case 'info':
        return 'bg-blue-500 border-blue-600';
      default:
        return 'bg-gray-500 border-gray-600';
    }
  };

  const getAlertIcon = (type: 'warning' | 'error' | 'info') => {
    switch (type) {
      case 'error':
        return '🚨';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '📢';
    }
  };

  // Atualizar timestamp de atividade
  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Eventos que indicam atividade do usuário
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Em desenvolvimento, sempre permitir acesso
  if (!isSecure && process.env.NODE_ENV === 'production') {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Ambiente Inseguro</h2>
            <p className="text-gray-600 mb-4">
              Detectamos um ambiente inseguro. Por favor, use uma conexão HTTPS para acessar o sistema.
            </p>
            <button
              onClick={() => window.location.href = 'https://' + window.location.host}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Usar Conexão Segura
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Alertas de Segurança */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
        {securityAlerts.map((alert) => (
          <div
            key={alert.timestamp}
            className={`${getAlertColor(alert.type)} text-white p-3 rounded-lg shadow-lg border flex items-start space-x-2 animate-pulse`}
          >
            <span className="text-xl">{getAlertIcon(alert.type)}</span>
            <div className="flex-1">
              <p className="text-sm font-medium">{alert.message}</p>
              <p className="text-xs opacity-75">
                {new Date(alert.timestamp).toLocaleTimeString()}
              </p>
            </div>
            <button
              onClick={() => dismissAlert(alert.timestamp)}
              className="text-white hover:text-gray-200 text-xl leading-none"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Conteúdo da Aplicação */}
      {children}
      
      {/* Modal de Sessão Expirada */}
      <AlertModal
        isOpen={showSessionModal}
        onClose={() => setShowSessionModal(false)}
        type="info"
        title="🔒 Sessão Expirada!"
        message="Sua sessão expirou por motivos de segurança. Essa é uma medida de proteção para evitar fraudes e garantir que apenas você tenha acesso à sua conta. Por favor, faça login novamente para continuar."
        confirmText="Fazer Login"
        onConfirm={handleSessionRedirect}
      />
      
      {/* Indicador de Segurança (apenas em desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs flex items-center space-x-1">
          <span>🔐</span>
          <span>Segurança Ativa</span>
        </div>
      )}
    </div>
  );
};

// Hook personalizado para validações de segurança
export const useSecurity = () => {
  const validateInput = (input: string, type: 'email' | 'text' | 'number' = 'text') => {
    const sanitized = securityService.sanitizeInput(input);
    
    switch (type) {
      case 'email':
        return securityService.validateEmail(sanitized);
      case 'number':
        return { valid: /^\d+$/.test(sanitized), error: 'Apenas números permitidos' };
      default:
        return { valid: sanitized.length > 0, error: 'Campo obrigatório' };
    }
  };

  const generateSecureToken = () => {
    return securityService.generateCSRFToken();
  };

  const getSecurityStats = () => {
    return securityService.getSecurityStats();
  };

  return {
    validateInput,
    generateSecureToken,
    getSecurityStats
  };
};

export default SecurityMiddleware;
